import { formatFHIRDate } from '@ctw/shared/utils/dates';
import type { Basic, Resource } from 'fhir/r4';
import { compact, find, orderBy, some, startCase, upperFirst } from 'lodash-es';
import {
  DATA_PROVIDER_DISPLAY_NAMES,
  FIRST_PARTY_EXTERNAL_SOURCE_DISPLAY,
} from '../mappings/data-provider-names';
import {
  CTW_EXTENSION_LENS_AGGREGATED_FROM,
  LENS_EXTENSION_AGGREGATED_FROM,
  SYSTEM_ENRICHMENT,
  SYSTEM_SUMMARY,
  SYSTEM_ZUS_CREATED_AT,
  SYSTEM_ZUS_OWNER,
  SYSTEM_ZUS_PROFILE_ACTION,
  SYSTEM_ZUS_THIRD_PARTY,
  SYSTEM_ZUS_UNIVERSAL_ID,
} from '../system-urls';
import { type ResourceMap, type ResourceTypeString, isFHIRDomainResource } from '../types';

export abstract class FHIRModel<T extends Resource> {
  public resource: T;

  // This is a string that is used to identify the type of model and
  // allows us to use a switch statement to determine the type of model.
  public abstract readonly kind: string;

  public readonly includedResources?: ResourceMap;

  private basicResources?: Array<Basic>;

  private optimisticIsRead?: boolean;

  private optimisticIsDismissed?: boolean;

  public constructor(resource: T, includedResources?: ResourceMap, basics?: Array<Basic>) {
    this.resource = resource;
    this.includedResources = includedResources;
    this.basicResources = basics;
  }

  // Returns the aggregated from resource ids for summary resources.
  public get aggregatedFromIds(): Array<string> {
    if (isFHIRDomainResource(this.resource)) {
      const extension = find(
        this.resource.extension,
        (x) =>
          x.url === LENS_EXTENSION_AGGREGATED_FROM || x.url === CTW_EXTENSION_LENS_AGGREGATED_FROM,
      );
      return compact(extension?.extension?.map((e) => e.valueReference?.reference?.split('/')[1]));
    }
    return [];
  }

  public get createdAt(): string | undefined {
    return find(this.resource.meta?.extension, { url: SYSTEM_ZUS_CREATED_AT })?.valueInstant;
  }

  public get createdAtDisplay(): string | undefined {
    return this.createdAt ? formatFHIRDate(this.createdAt) : undefined;
  }

  public get lastUpdated(): string | undefined {
    return this.resource.meta?.lastUpdated || this.createdAt;
  }

  public get id(): string {
    return this.resource.id || '';
  }

  // key is used to uniquely identify rows in our resource table
  public get key(): string {
    return this.id;
  }

  public get versionId(): string {
    return this.resource.meta?.versionId || '';
  }

  public get isDismissed(): boolean {
    if (this.optimisticIsDismissed !== undefined) {
      return this.optimisticIsDismissed;
    }

    const basic = this.getLatestAction(['archive', 'unarchive']);
    return some(basic?.code.coding, {
      system: SYSTEM_ZUS_PROFILE_ACTION,
      code: 'archive',
    });
  }

  // Special key for filtering. See utils/filters.ts.
  public get isHidden(): boolean {
    return this.isDismissed;
  }

  // Optimistically toggle the isDismiss state.
  // This is useful to be able to change the state of the UI
  // without having to wait for the toggleRead API call.
  public optimisticToggleIsDismiss(): void {
    this.optimisticIsDismissed = !this.isDismissed;
  }

  public get isRead(): boolean {
    if (this.optimisticIsRead !== undefined) {
      return this.optimisticIsRead;
    }
    const basic = this.getLatestAction(['read', 'unread']);
    return some(basic?.code.coding, {
      system: SYSTEM_ZUS_PROFILE_ACTION,
      code: 'read',
    });
  }

  // Optimistically toggle the isRead state.
  // This is useful to be able to change the state of the UI
  // without having to wait for the toggleRead API call.
  public optimisticToggleIsRead(): void {
    this.optimisticIsRead = !this.isRead;
  }

  // Returns true if this resource is a summary/lens resource.
  public get isSummaryResource(): boolean {
    return find(this.resource.meta?.tag, { system: SYSTEM_SUMMARY }) !== undefined;
  }

  public get isExternalNetworkData(): boolean {
    return this.resource.meta?.tag?.some((t) => t.system === SYSTEM_ZUS_THIRD_PARTY) ?? false;
  }

  // If the data came from a third party data source then return that name
  public get externalNetworkSourceName(): string | undefined {
    return this.resource.meta?.tag?.find((t) => t.system === SYSTEM_ZUS_THIRD_PARTY)?.code;
  }

  // Override in subclasses that can resolve the patient's managing organization.
  public get patientOrganizationName(): string | undefined {
    return undefined;
  }

  /** Human-readable data source label for UI (e.g. "Lens", "CommonWell", "Main St Health (Zus Network)"). */
  public getDataSourceDisplay(builderId: string): string {
    if (this.isSummaryResource) {
      return 'Lens';
    }

    if (this.isExternalNetworkData) {
      const rawName = this.externalNetworkSourceName ?? '';
      const networkName = DATA_PROVIDER_DISPLAY_NAMES[rawName] ?? upperFirst(rawName);
      const orgName = this.patientOrganizationName;
      if (orgName) {
        return `${orgName} (${networkName})`;
      }
      return networkName;
    }

    if (this.ownedByBuilder(builderId)) {
      const orgName = this.patientOrganizationName;
      return orgName ?? '1st Party Data';
    }

    const orgName = this.patientOrganizationName;
    const sourceDisplay = FIRST_PARTY_EXTERNAL_SOURCE_DISPLAY;
    return orgName ? `${orgName} (${sourceDisplay})` : `Unknown Organization (${sourceDisplay})`;
  }

  public get patientUPID(): string | undefined {
    if (isFHIRDomainResource(this.resource)) {
      return find(this.resource.extension, { url: SYSTEM_ZUS_UNIVERSAL_ID })?.valueString;
    }
    return undefined;
  }

  public get resourceType(): ResourceTypeString {
    return this.resource.resourceType as ResourceTypeString;
  }

  public get resourceTypeTitle(): string {
    return startCase(this.resourceType);
  }

  public get lastUpdatedDisplay(): string {
    return this.lastUpdated ? (formatFHIRDate(this.lastUpdated as string) ?? '') : '';
  }

  public abstract get title(): string;

  public get basics(): Array<Basic> | undefined {
    return this.basicResources;
  }

  public set basics(basics: Array<Basic> | undefined) {
    this.basicResources = basics;
  }

  // Returns the latest Basic resource that has a code that matches
  // one of profileActions.
  // In practice, there should only be a single Basic resource, but
  // we need to handle the case where there may be several.
  public getLatestAction(profileActions: Array<string>): Basic | undefined {
    const ordered = orderBy(this.basics, 'meta.lastUpdated', 'desc') as Array<Resource>;
    return find(ordered, (resource) =>
      some(
        (resource as Basic).code.coding,
        (coding) =>
          coding.system === SYSTEM_ZUS_PROFILE_ACTION && profileActions.includes(coding.code ?? ''),
      ),
    ) as Basic | undefined;
  }

  // Returns true if this resource is enriched with additional data
  // from Zus enrichment.
  public isEnriched(): boolean {
    return JSON.stringify(this.resource).includes(SYSTEM_ENRICHMENT);
  }

  // Returns true if this resource is owned by the specified builder.
  public ownedByBuilder(builderId: string): boolean {
    if (this.resource.meta?.tag?.some((tag) => tag.system === SYSTEM_ZUS_THIRD_PARTY)) {
      return false;
    }
    const ownerTag = this.resource.meta?.tag?.find((tag) => tag.system === SYSTEM_ZUS_OWNER);
    const splitTag = ownerTag?.code?.split('/');

    if (splitTag?.length !== 2) {
      return false;
    }
    return splitTag[1] === builderId;
  }

  // Returns a string that would setup this model.
  //   E.g. "new ConditionModel({...resource})"
  // Helpful for debugging / console logging.
  // This way we get copyable code for the model
  // instead of "[object Object]".
  public toString() {
    const modelName = this.constructor.name;
    return `new ${modelName}(${JSON.stringify(this.resource, null, 2)}\n)`;
  }
}
